/**
 * G2eGente - Resposta Pública Pesquisa de Clima (Sem Cadastro)
 * Permite que qualquer pessoa responda à pesquisa via link
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  ThermometerSun,
  Building2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Lock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicClimateResponse() {
  const { codigo } = useParams();
  
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchSurvey();
  }, [codigo]);

  const fetchSurvey = async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/climate/${codigo}`);
      setSurvey(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Pesquisa não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (value) => {
    const pergunta = survey.perguntas[currentQuestion];
    setAnswers(prev => ({
      ...prev,
      [pergunta.id]: parseInt(value)
    }));
  };

  const handleNext = () => {
    if (currentQuestion < survey.perguntas.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const unanswered = survey.perguntas.filter(p => !answers[p.id]);
    if (unanswered.length > 0) {
      toast.error(`Responda todas as perguntas. Faltam ${unanswered.length}.`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = Object.entries(answers).map(([pergunta_id, valor]) => ({
        pergunta_id,
        valor
      }));

      await axios.post(`${API}/api/public/climate/${codigo}/responder`, payload);
      setSubmitted(true);
      toast.success('Resposta enviada com sucesso!');
    } catch (err) {
      toast.error('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Pesquisa Indisponível</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Obrigado!</h2>
            <p className="text-slate-600 mb-6">
              Sua resposta foi enviada com sucesso. Sua participação contribui para 
              melhorar o ambiente de trabalho.
            </p>
            <p className="text-sm text-slate-500">
              Você pode fechar esta página agora.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pergunta = survey.perguntas[currentQuestion];
  const progress = ((currentQuestion + 1) / survey.perguntas.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-teal-50 p-4">
      <div className="max-w-2xl mx-auto pt-4">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <ThermometerSun className="w-7 h-7 text-cyan-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-1">{survey.titulo}</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
            <Building2 className="w-4 h-4" />
            <span>{survey.empresa}</span>
          </div>
        </div>

        {/* Privacy Notice (first question only) */}
        {currentQuestion === 0 && (
          <Card className="mb-4 bg-emerald-50 border-emerald-200">
            <CardContent className="p-3">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-700">
                  Suas respostas são 100% anônimas. Responda com sinceridade.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>{currentQuestion + 1} de {survey.perguntas.length}</span>
            <span>{answeredCount} respondidas</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-4">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-6">
              {pergunta.texto}
            </h3>

            <RadioGroup
              value={answers[pergunta.id]?.toString() || ''}
              onValueChange={handleAnswer}
              className="space-y-3"
            >
              {[1, 2, 3, 4, 5].map(value => (
                <label
                  key={value}
                  className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    answers[pergunta.id] === value
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-slate-200 hover:border-cyan-300'
                  }`}
                >
                  <RadioGroupItem value={value.toString()} />
                  <span className={`font-medium ${answers[pergunta.id] === value ? 'text-cyan-800' : 'text-slate-700'}`}>
                    {value === 1 && 'Discordo totalmente'}
                    {value === 2 && 'Discordo'}
                    {value === 3 && 'Neutro'}
                    {value === 4 && 'Concordo'}
                    {value === 5 && 'Concordo totalmente'}
                  </span>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>

          {currentQuestion < survey.perguntas.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!answers[pergunta.id]}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Próxima
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < survey.perguntas.length}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-survey-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Enviar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
